import { Box, Button, Collapse, FormControl, FormLabel } from '@mui/material';
import React, { useState } from 'react';
import { type Control, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { palette, spaces } from '../../../../assets/styles/themes/tokens';
import constants, { filterChipStyle, NONE_OF_THE_ABOVE_OPTION } from '../../../../constants';
import useFilterChipMaxHeight from '../../../../utils/useFilterChipMaxHeight';
import FilterChip from '../../../modals/SearchFiltersDialog/FilterChip';
import type { Option } from '../../../onboarding/OnboardingOptionChipList';

interface SubjectWrapperProps {
	value?: string[];
	onChange?: (value: string[]) => void;
	name?: string;
}

const SubjectWrapper = ({ value = [], onChange = () => {} }: SubjectWrapperProps) => {
	const { t } = useTranslation();
	const [areOptionsTruncated, setAreOptionsTruncated] = useState(true);

	const subjectList: Option[] = constants.subjects
		.filter((subject) => subject.id !== NONE_OF_THE_ABOVE_OPTION)
		.map((subject) => ({
			name: subject.text,
			value: subject.id,
			isSelected: false,
			iconName: subject.iconName,
		}))
		.sort((a, b) => a.name.localeCompare(b.name));

	const maxHeight = useFilterChipMaxHeight({ chipStyle: filterChipStyle });

	const handleShowAllClick = () => setAreOptionsTruncated(!areOptionsTruncated);

	const handleSubjectClick = (subjectId: string) => {
		const isSelected = value.includes(subjectId);
		if (isSelected) {
			onChange(value.filter((id) => id !== subjectId));
		} else {
			onChange([...value, subjectId]);
		}
	};

	return (
		<Box
			sx={{
				display: 'flex',
				gap: spaces.xs,
				flexWrap: 'wrap',
				overflow: 'hidden',
			}}
		>
			<FormControl fullWidth>
				<Collapse
					in={!areOptionsTruncated}
					collapsedSize={maxHeight}
					data-testid="filter-chip-collapse-subjects"
				>
					<Box
						sx={{
							display: 'flex',
							gap: `${filterChipStyle.gap}px`,
							flexWrap: 'wrap',
							overflow: 'hidden',
						}}
					>
						{subjectList.map((option) => (
							<Box key={option.value} data-testid={`filter-chip-${option.value}`}>
								<FilterChip
									isSelected={value.includes(option.value)}
									value={option.value}
									label={option.name}
									onClick={() => handleSubjectClick(option.value)}
								/>
							</Box>
						))}
					</Box>
				</Collapse>
			</FormControl>
			{
				<Box>
					<Button
						color="primary"
						size="medium"
						onClick={handleShowAllClick}
						sx={{
							marginLeft: `-${spaces.xs}`,
						}}
					>
						{areOptionsTruncated ? t('Show all <total>', { total: subjectList.length }) : t('Show less')}
					</Button>
				</Box>
			}
		</Box>
	);
};

const CollectionSubjects = ({
	control,
	initialValue,
}: {
	control: Control<any>;
	initialValue?: string[];
}) => {
	const { t } = useTranslation();

	return (
		<Box>
			<FormLabel sx={{ color: palette.text.primary }}>{t('Subject')}</FormLabel>
			<Controller
				name="subjects"
				control={control}
				defaultValue={initialValue || []}
				as={<SubjectWrapper />}
			/>
		</Box>
	);
};

export default CollectionSubjects;
